import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Eye, Target } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { t } from "@/lib/translations";

const CONGO_CHILDREN = [
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-07/prod_agent_8873ce71-5b0c-4935-a990-b42d3a2b9b9c/IMG_0649_57924384b05d47e795e0a27c8bb791c5.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-07/prod_agent_8873ce71-5b0c-4935-a990-b42d3a2b9b9c/IMG_0639_942c4529c348434bbdd88ecb9864accd.jpeg",
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-07/prod_agent_8873ce71-5b0c-4935-a990-b42d3a2b9b9c/IMG_0659_5c3aa17b3dfd4ddcaebd3599d4bd407a.jpeg",
];

export default function Mission() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { lang } = useLang();
  const tx = t[lang].mission;

  return (
    <section id="mission" className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, oklch(0.38 0.18 210) 0%, oklch(0.28 0.16 210) 60%, oklch(0.20 0.12 210) 100%)" }}>
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white/3 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="text-center mb-16">
          <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">{tx.tag}</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tx.title}</h2>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Vision + Congo children photos */}
          <motion.div initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.1 }}>
            {/* 3-photo mosaic */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              {CONGO_CHILDREN.map((src, i) => (
                <div key={i} className={`rounded-2xl overflow-hidden ${i === 0 ? "col-span-2 row-span-1 aspect-video" : "aspect-square"}`}>
                  <img src={src} alt="Enfants congolais" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="bg-white/8 border border-white/12 rounded-3xl p-8 space-y-4 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center"><Eye className="w-5 h-5 text-accent" /></div>
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tx.visionTitle}</h3>
              </div>
              <p className="text-white/75 leading-relaxed text-base">{tx.visionText}</p>
              <div className="pt-4 border-t border-white/10">
                <p className="text-accent/80 text-sm italic font-medium">"Ensemble, révélons les talents, redonnons espoir et construisons un avenir meilleur."</p>
              </div>
            </div>
          </motion.div>
          {/* Mission points */}
          <motion.div initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.25 }} className="space-y-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center"><Target className="w-5 h-5 text-accent" /></div>
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tx.missionTitle}</h3>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">{tx.missionIntro}</p>
            {tx.points.map((point: string, i: number) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.5, delay: 0.35 + i * 0.08 }}
                className="flex items-start gap-4 bg-white/5 border border-white/8 rounded-2xl p-4 hover:bg-white/8 hover:border-accent/20 transition-all duration-200">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-primary text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</div>
                <p className="text-white/80 text-sm leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
